import React, {useState} from "react";
import {useTranslation} from "@unibuddy/intl";
import {Label, Select, Stack} from "@unibuddy/patron";
import {useQuery} from "@apollo/client";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";

import {contentTopicsQuery} from "../data/contentTopicsQuery";
import {IntlNamespaces} from "../../../../translations/types";
import {INTL_TOPIC} from "../../../../translations/blogs";
import {INTL_NONE_SELECTED} from "../../../../translations/common";
import {INTL_NO_OPTIONS_MESSAGE} from "../../../../translations/mentorCard";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";

type Topic = {
    id: string;
    name: string;
};

type TopicInputProps = {
    currentTopic: string;
    selectableTopicIds: string[];
    onSearch: (filterInput: string, filterName: string) => void;
    containerLoading: boolean;
};

const EmptyTopicInput = () => {
    const {t} = useTranslation([IntlNamespaces.BLOGS, IntlNamespaces.MENTOR_CARD]);
    return (
        <Stack space="xsmall">
            <Label id="topic">{t(INTL_TOPIC.key, INTL_TOPIC.defaultValue)}</Label>
            <Select id="topic" value="" formatValue={(e: React.FormEvent<HTMLSelectElement>) => e}>
                <option value="">
                    {t(INTL_NO_OPTIONS_MESSAGE.key, INTL_NO_OPTIONS_MESSAGE.defaultValue)}
                </option>
            </Select>
        </Stack>
    );
};

export const TopicInput = ({
    currentTopic = "",
    selectableTopicIds,
    onSearch,
    containerLoading,
}: TopicInputProps) => {
    const {t} = useTranslation([IntlNamespaces.BLOGS, IntlNamespaces.COMMON]);
    const [selectedTopic, setSelectedTopic] = useState(currentTopic);
    const {trackEvent} = useAnalytics();
    const {slug: universitySlug} = useUniversity();

    const {data, loading, error} = useQuery(contentTopicsQuery, {
        variables: {
            idIn: selectableTopicIds,
        },
        skip: selectableTopicIds.length === 0,
    });

    if (selectableTopicIds.length === 0 && !containerLoading && !loading)
        return <EmptyTopicInput />;

    const topics = data?.blogTopics ?? [];

    const onChange = (event: {target: {value: string}}) => {
        setSelectedTopic(event.target.value);
        onSearch(event.target.value, "topics");
        trackEvent(sharedEvents.contentFilteredByTopic.name, {
            universitySlug,
            topicNames: event.target.value,
        });
    };

    if (error) return <EmptyTopicInput />;

    return (
        <Stack space="xsmall">
            <Label id="topic">{t(INTL_TOPIC.key, INTL_TOPIC.defaultValue)}</Label>
            <Select
                id="topic"
                value={selectedTopic}
                onChange={onChange}
                formatValue={(e: React.FormEvent<HTMLSelectElement>) => e}
            >
                <option value="">
                    {t(INTL_NONE_SELECTED.key, INTL_NONE_SELECTED.defaultValue)}
                </option>
                {topics &&
                    topics.map((topic: Topic) => (
                        <option value={topic.id} key={topic.id}>
                            {topic.name}
                        </option>
                    ))}
            </Select>
        </Stack>
    );
};
